/**
 * Definition Coverage Panel (Spec 205 — US1 visibility + US2 entry)
 *
 * Lists workspaces with captured definitions (coverage counts + latest point), and launches a
 * workspace-complete restore. Entity-first read via the candidates endpoint.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 205-workspace-complete-dr
 */

import React, { useMemo, useState } from 'react';
import {
  Card,
  DataTable,
  type DataTableColumn,
  Button,
  Badge,
  Input,
  Select,
  FilterBar,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { formatEastern } from '@/utils/dateFormatter';
import { useWorkspaceRestoreCandidates } from '../../hooks/useWorkspaceRestore';
import { WorkspaceRestoreCandidate } from '../../api/workspaceRestore';
import { WorkspaceRestoreModal } from './WorkspaceRestoreModal';
import { WorkspaceRecoveryBrowser } from './WorkspaceRecoveryBrowser';
import { useAuth } from '../../hooks/useAuth';

export const DefinitionCoveragePanel: React.FC = () => {
  // Spec 214 FR-028: workspace-restore launch is Admin+Operator server-side —
  // read roles get Explore only (their Restore clicks 403'd).
  const { isOperatorOrAbove } = useAuth();
  const canRestore = isOperatorOrAbove();
  const { data, isLoading, isError, error, refetch } = useWorkspaceRestoreCandidates();
  const [selected, setSelected] = useState<WorkspaceRestoreCandidate | null>(null);
  const [exploring, setExploring] = useState<WorkspaceRestoreCandidate | null>(null);
  // Search + filter on top (mirrors the lakehouse/warehouse restore so both behave the same).
  const [q, setQ] = useState('');
  const [restorableOnly, setRestorableOnly] = useState('');
  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data ?? []).filter(
      (r) =>
        (!ql || (r.workspaceName ?? '').toLowerCase().includes(ql) || r.workspaceId.toLowerCase().includes(ql)) &&
        (restorableOnly !== 'yes' || r.latestDefinitionPoint?.restorableDefinitions)
    );
  }, [data, q, restorableOnly]);

  const columns: DataTableColumn<WorkspaceRestoreCandidate>[] = [
    { key: 'workspaceName', header: 'Workspace', wrap: true },
    {
      key: 'assets',
      header: 'Assets',
      render: (r) => `${r.lakehouses} LH · ${r.warehouses} WH`,
      sortable: false,
    },
    {
      key: 'captured',
      header: 'Definitions',
      render: (r) =>
        r.latestDefinitionPoint ? (
          <span className="flex items-center gap-1">
            {/* 0 captured definitions is not a protective backup — show it neutral, not green. */}
            <Badge variant={r.latestDefinitionPoint.supportedCount > 0 ? 'success' : 'default'}>
              {r.latestDefinitionPoint.supportedCount} captured
            </Badge>
            {r.latestDefinitionPoint.unsupportedCount > 0 && (
              <Badge variant="warning">{r.latestDefinitionPoint.unsupportedCount} unsup</Badge>
            )}
            {r.latestDefinitionPoint.failedCount > 0 && (
              <Badge variant="danger">{r.latestDefinitionPoint.failedCount} failed</Badge>
            )}
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
      sortable: false,
    },
    {
      key: 'pointTime',
      header: 'Latest capture',
      render: (r) =>
        r.latestDefinitionPoint
          ? formatEastern(r.latestDefinitionPoint.pointTime)
          : '—',
      sortValue: (r) => r.latestDefinitionPoint?.pointTime ?? '',
    },
    {
      key: 'restorable',
      header: 'Restorable',
      render: (r) =>
        r.latestDefinitionPoint?.restorableDefinitions ? (
          <Badge variant="success">Yes</Badge>
        ) : (
          <Badge variant="warning">No</Badge>
        ),
      sortable: false,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      sortable: false,
      width: '15rem',
      render: (r) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={!r.latestDefinitionPoint}
            onClick={() => setExploring(r)}
          >
            Explore
          </Button>
          {canRestore && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!r.latestDefinitionPoint?.restorableDefinitions}
              onClick={() => setSelected(r)}
            >
              Restore all → new
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState message={(error as Error)?.message ?? 'Failed to load'} onRetry={() => refetch()} />;
  if (!data?.length) return <EmptyState title="No workspaces have captured definitions yet" />;

  return (
    <Card>
      <div className="space-y-3 p-4">
        <FilterBar>
          <div className="w-72">
            <Input
              inputSize="sm"
              isFullWidth
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search workspace name or id…"
            />
          </div>
          <div className="w-40">
            <Select selectSize="sm" isFullWidth value={restorableOnly} onChange={(e) => setRestorableOnly(e.target.value)}>
              <option value="">All</option>
              <option value="yes">Restorable only</option>
            </Select>
          </div>
          <span className="text-sm text-[var(--color-text-secondary)]">{rows.length} of {data?.length ?? 0}</span>
        </FilterBar>
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.workspaceId} />
      </div>
      {selected && (
        <WorkspaceRestoreModal
          open={!!selected}
          onClose={() => setSelected(null)}
          candidate={selected}
        />
      )}
      {exploring && (
        <WorkspaceRecoveryBrowser
          open={!!exploring}
          onClose={() => setExploring(null)}
          candidate={exploring}
        />
      )}
    </Card>
  );
};

export default DefinitionCoveragePanel;
